import React, { useState } from 'react';
import { useToast, NotificationItem } from '../../contexts/ToastContext';
import { useTheme } from '../../contexts/ThemeContext';

interface NotificationsHistoryProps {
  onClose?: () => void;
  maxHeight?: string;
  compact?: boolean;
}

export const NotificationsHistory: React.FC<NotificationsHistoryProps> = ({ 
  onClose,
  maxHeight = '70vh',
  compact = false
}) => {
  const { notifications, markAsRead, markAllAsRead, clearNotifications, unreadCount } = useToast();
  const { darkMode } = useTheme();
  const [filter, setFilter] = useState<'all' | 'unread' | 'success' | 'error' | 'warning' | 'info'>('all');
  
  // Filter notifications based on selected filter
  const filteredNotifications = notifications.filter(notification => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !notification.read;
    return notification.type === filter;
  });
  
  // Format timestamp
  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };
  
  // Get icon for notification type
  const getIcon = (type: string) => {
    switch (type) {
      case 'success':
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        );
      case 'error':
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        );
      case 'warning':
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      case 'info':
      default:
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        );
    }
  };
  
  // Get color class for notification type
  const getColorClass = (type: string, isDark: boolean) => {
    switch (type) {
      case 'success':
        return isDark ? 'text-green-400' : 'text-green-600';
      case 'error':
        return isDark ? 'text-red-400' : 'text-red-600';
      case 'warning':
        return isDark ? 'text-yellow-400' : 'text-yellow-600';
      case 'info':
      default:
        return isDark ? 'text-blue-400' : 'text-blue-600';
    }
  };
  
  // Get background color class for notification type
  const getBgColorClass = (type: string, isDark: boolean) => {
    switch (type) {
      case 'success':
        return isDark ? 'bg-green-900 border-green-700' : 'bg-green-50 border-green-200';
      case 'error':
        return isDark ? 'bg-red-900 border-red-700' : 'bg-red-50 border-red-200';
      case 'warning':
        return isDark ? 'bg-yellow-900 border-yellow-700' : 'bg-yellow-50 border-yellow-200';
      case 'info':
      default:
        return isDark ? 'bg-blue-900 border-blue-700' : 'bg-blue-50 border-blue-200';
    }
  };
  
  return (
    <div className={`${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
      <div className="flex justify-between items-center mb-4">
        <h2 className={`text-xl font-bold ${compact ? 'text-lg' : ''}`}>
          Notifications
          {unreadCount > 0 && (
            <span className="ml-2 text-sm bg-red-600 text-white px-2 py-0.5 rounded-full">
              {unreadCount} new
            </span>
          )}
        </h2>
        <div className="flex space-x-2">
          <button
            onClick={markAllAsRead}
            className={`px-3 py-1 text-sm rounded ${
              darkMode 
                ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                : 'bg-blue-500 hover:bg-blue-600 text-white'
            }`}
          >
            Mark All Read
          </button>
          <button
            onClick={clearNotifications}
            className={`px-3 py-1 text-sm rounded ${
              darkMode 
                ? 'bg-red-600 hover:bg-red-700 text-white' 
                : 'bg-red-500 hover:bg-red-600 text-white'
            }`}
          >
            Clear All
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className={`px-3 py-1 text-sm rounded ${
                darkMode 
                  ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
              }`}
            >
              Close
            </button>
          )}
        </div>
      </div>
      
      {/* Filter tabs */}
      <div className="flex space-x-2 mb-4 overflow-x-auto pb-2">
        {(['all', 'unread', 'success', 'error', 'warning', 'info'] as const).map((filterType) => (
          <button
            key={filterType}
            onClick={() => setFilter(filterType)}
            className={`px-3 py-1 text-sm rounded whitespace-nowrap ${
              filter === filterType
                ? darkMode 
                  ? 'bg-gray-700 text-white' 
                  : 'bg-gray-200 text-gray-800'
                : darkMode 
                  ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {filterType.charAt(0).toUpperCase() + filterType.slice(1)}
            {filterType === 'all' && ` (${notifications.length})`}
            {filterType === 'unread' && ` (${unreadCount})`}
          </button>
        ))}
      </div>
      
      {/* Notifications list */}
      <div className={`space-y-2 overflow-y-auto pr-1`} style={{ maxHeight }}>
        {filteredNotifications.length === 0 ? (
          <div className={`p-4 text-center rounded ${
            darkMode ? 'bg-gray-800' : 'bg-gray-100'
          }`}>
            No notifications to display
          </div>
        ) : (
          filteredNotifications.map((notification) => (
            <div 
              key={notification.id}
              className={`p-3 rounded-lg border ${
                getBgColorClass(notification.type, darkMode)
              } ${
                !notification.read ? 'border-l-4' : ''
              } cursor-pointer`}
              onClick={() => !notification.read && markAsRead(notification.id)}
            >
              <div className="flex items-start">
                <div className={`mr-2 ${getColorClass(notification.type, darkMode)}`}>
                  {getIcon(notification.type)}
                </div>
                <div className="flex-1">
                  <div className={`${!notification.read ? 'font-semibold' : ''}`}>
                    {notification.message}
                  </div>
                  <div className={`text-xs mt-1 ${
                    darkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    {formatTime(notification.timestamp)}
                  </div>
                </div>
                {!notification.read && (
                  <div 
                    className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                      darkMode ? 'bg-blue-700 text-blue-100' : 'bg-blue-100 text-blue-800'
                    }`}
                  >
                    New
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default NotificationsHistory;
